/**
 * Standard cancellation fee presets. The real policy is judgment-based (notice given,
 * repeat cancellations): a flat $50 or $100, or 50% / 100% of the job's booked price.
 * Percentages are resolved server-side from the Zoho sales order (applyCancellationFee);
 * the dialog uses the same base (rate × quantity of the SO lines) to preview the dollars.
 */
import { useEffect, useState } from 'react';
import { getServiceOrder } from '../services/zohoServiceOrderService';

export type StandardFeePreset = 'flat50' | 'flat100' | 'pct50' | 'pct100';

export const STANDARD_FEE_PRESETS: ReadonlyArray<{ id: StandardFeePreset; fee: number | { percent: number } }> = [
  { id: 'flat50', fee: 50 },
  { id: 'flat100', fee: 100 },
  { id: 'pct50', fee: { percent: 50 } },
  { id: 'pct100', fee: { percent: 100 } },
];

export function presetFee(id: StandardFeePreset): number | { percent: number } {
  return STANDARD_FEE_PRESETS.find(p => p.id === id)!.fee;
}

/** Dollar label for a preset, e.g. "$50.00" or "$125.00 (50%)"; "…" while the job price is loading, "50%" if unknown. */
export function presetLabel(fee: number | { percent: number }, jobPrice: number | null | undefined): string {
  if (typeof fee === 'number') return `$${fee.toFixed(2)}`;
  if (jobPrice === undefined) return `${fee.percent}%`;
  if (jobPrice === null) return `${fee.percent}%`;
  return `$${(Math.round(jobPrice * fee.percent) / 100).toFixed(2)} (${fee.percent}%)`;
}

/**
 * The job's booked price (sum of rate × quantity on the Zoho SO), fetched while `enabled`.
 * undefined = loading, null = unavailable (no SO / lines already zeroed).
 */
export function useJobPrice(bookingId: string | undefined, enabled: boolean): number | null | undefined {
  const [price, setPrice] = useState<number | null | undefined>(undefined);
  useEffect(() => {
    if (!enabled || !bookingId) return;
    let cancelled = false;
    setPrice(undefined);
    getServiceOrder(bookingId)
      .then(so => {
        if (cancelled) return;
        const base = (so?.line_items ?? []).reduce((sum, i) => sum + Number(i.rate ?? 0) * Number(i.quantity ?? 1), 0);
        setPrice(base > 0 ? base : null);
      })
      .catch(() => { if (!cancelled) setPrice(null); });
    return () => { cancelled = true; };
  }, [bookingId, enabled]);
  return price;
}
